"use client";

// src/ui/components/calculator/calculator-form.tsx
//
// INPUTS-ONLY column of the Peptidrechner. Fully controlled — owns NO state.
// The peptide picker, the result Cockpit and the buy-bridge now live in
// client.tsx so the page can compose a sticky-cockpit grid (result stays
// visible while the inputs scroll). This component renders ONLY the input
// fields: vial size, BAC water, dose (+range hint + mcg/mg toggle), syringe.

import { useId } from "react";
import { useTranslations } from "next-intl";
import { Label } from "@/ui/components/ui/label";
import { PresetChips } from "@/ui/components/calculator/preset-chips";
import { ReverseRecipe } from "@/ui/components/calculator/reverse-recipe";
import { accentClassFor } from "@/ui/components/calculator/cockpit";
import { getPeptideBySlug, getPeptideCalculatorMode } from "@/lib/calculator/peptide-data";
import { isFiveProductStudyContextSlug } from "@/config/five-product-launch";
import type { Locale } from "@/config/locale";
import type { CalculatorState } from "@/lib/calculator/use-calculator-state";
import type { SyringeSize, DoseUnitMode, ReverseProfile } from "@/lib/calculator/types";

const SYRINGE_OPTIONS: { size: SyringeSize; labelIU: number }[] = [
	{ size: 0.3, labelIU: 30 },
	{ size: 0.5, labelIU: 50 },
	{ size: 1.0, labelIU: 100 },
];

const BAC_PRESETS = [1, 2, 2.5, 3];

// Native-unit conversion is DISPLAY-ONLY: dose stays internal mg in state.
function mgToNative(mg: number, mode: DoseUnitMode): number {
	return mode === "mcg" ? Math.round(mg * 1000) : mg;
}
function nativeToMg(value: number, mode: DoseUnitMode): number {
	return mode === "mcg" ? value / 1000 : value;
}

/**
 * Dose presets in NATIVE unit. Falls back to [min, midpoint, max] from doseRange
 * (rounded) when no peptide-specific dosePresets exist.
 */
function nativeDosePresets(
	dosePresets: number[] | undefined,
	doseRange: { min: number; max: number } | undefined,
	mode: DoseUnitMode,
): number[] {
	const roundNative = (v: number) => (mode === "mcg" ? Math.round(v * 1000) : Math.round(v * 100) / 100);
	if (dosePresets?.length) return dosePresets.map((mg) => roundNative(mg));
	if (doseRange) {
		const mid = (doseRange.min + doseRange.max) / 2;
		const set = Array.from(new Set([doseRange.min, mid, doseRange.max].map(roundNative)));
		return set;
	}
	return [];
}

interface CalculatorFormProps {
	// Controlled input state (owned by useCalculatorState in PeptideCalculatorClient).
	state: CalculatorState;
	locale: string;
	onPeptideAmountChange: (value: number) => void;
	onBacWaterChange: (value: number) => void;
	onDoseChange: (value: number) => void;
	onSyringeSizeChange: (value: SyringeSize) => void;
	onDoseUnitModeChange: (value: DoseUnitMode) => void;
	onReverseProfileChange: (value: ReverseProfile) => void;
}

export function CalculatorForm({
	state,
	locale,
	onPeptideAmountChange,
	onBacWaterChange,
	onDoseChange,
	onSyringeSizeChange,
	onDoseUnitModeChange,
	onReverseProfileChange,
}: CalculatorFormProps) {
	const t = useTranslations("peptideCalculator");
	const doseUnitGroupName = useId();

	const { peptideSlug, peptideAmount, bacWater, dose, syringeSize, doseUnitMode, mode, reverseProfile } =
		state;
	const currentPeptide = getPeptideBySlug(peptideSlug);
	const isResearchDilution = getPeptideCalculatorMode(currentPeptide) === "research-dilution";
	const hasNeutralResearchUi = isResearchDilution || isFiveProductStudyContextSlug(peptideSlug);
	const doseRange = currentPeptide?.doseRange;
	const accentClass = currentPeptide ? accentClassFor(currentPeptide, locale as Locale) : "text-success";

	// Vial sizes stockable in the shop → green dot on those chips.
	const shopVialValues =
		currentPeptide?.hasBergdorfProduct && currentPeptide.defaultVialSizes
			? currentPeptide.defaultVialSizes
			: undefined;

	// Native-unit dose plumbing.
	const doseNative = mgToNative(dose, doseUnitMode);
	const dosePresetsNative = nativeDosePresets(currentPeptide?.dosePresets, doseRange, doseUnitMode);
	const handleDoseNativeChange = (nativeValue: number) => onDoseChange(nativeToMg(nativeValue, doseUnitMode));
	const doseRangeNative = doseRange
		? {
				min: mgToNative(doseRange.min, doseUnitMode),
				max: mgToNative(doseRange.max, doseUnitMode),
			}
		: null;
	const doseUnitLabel = doseUnitMode === "mcg" ? "mcg" : "mg";
	const doseFieldLabel = hasNeutralResearchUi
		? `${t("researchDilution.targetSampleAmount")} (${doseUnitLabel})`
		: t("form.dose").replace("(mg)", `(${doseUnitLabel})`);

	return (
		<div className="space-y-3 sm:space-y-5">
			{/* Peptide amount — vial chips (green dot when stockable) + custom */}
			<div className="space-y-1.5">
				<Label className="text-xs sm:text-sm">{t("form.peptideAmount")}</Label>
				{currentPeptide ? (
					<PresetChips
						key={`vial-${peptideSlug}`}
						values={currentPeptide.defaultVialSizes}
						selected={peptideAmount}
						onChange={onPeptideAmountChange}
						unit="mg"
						accessibleLabel={t("form.peptideAmount")}
						min={0}
						shopValues={shopVialValues}
					/>
				) : (
					<PresetChips
						key="vial-custom"
						values={[2, 5, 10, 15, 20]}
						selected={peptideAmount}
						onChange={onPeptideAmountChange}
						unit="mg"
						accessibleLabel={t("form.peptideAmount")}
						min={0}
					/>
				)}
			</div>

			{/* BAC water (forward) — chips + custom. In reverse mode the field is
			    replaced by the mixing-helper profile cards + recipe, which DERIVES
			    the water volume from vial + dose instead of asking for it. */}
			{!hasNeutralResearchUi && mode === "reverse" ? (
				<ReverseRecipe
					vial={peptideAmount}
					dose={dose}
					profile={reverseProfile}
					onProfileChange={onReverseProfileChange}
					accentClassName={accentClass}
				/>
			) : (
				<div className="space-y-1.5">
					<Label className="text-xs sm:text-sm">{t("form.bacWater")}</Label>
					<PresetChips
						key={`bac-${peptideSlug}`}
						values={BAC_PRESETS}
						selected={bacWater}
						onChange={onBacWaterChange}
						unit="ml"
						accessibleLabel={t("form.bacWater")}
						min={0.1}
					/>
				</div>
			)}

			{/* Dose or neutral target-sample amount — native-unit chips + optional range + unit toggle. */}
			<div className="space-y-1.5">
				{/* Label + range hint + mcg/mg toggle. Wraps to a second line on the narrowest
				    phones (flex-wrap) so a long dose-range string never collides with the unit
				    toggle or overflows the card; aligns on one row from xs up. */}
				<div className="flex flex-wrap items-end justify-between gap-x-2 gap-y-1">
					<Label className="text-xs sm:text-sm">{doseFieldLabel}</Label>
					<div className="flex items-center gap-2">
						{!hasNeutralResearchUi && doseRangeNative && (
							<span className="text-right text-[11px] font-normal text-muted-foreground">
								{t("form.doseRange", {
									min: doseRangeNative.min,
									max: doseRangeNative.max,
									unit: doseUnitLabel,
								})}
							</span>
						)}
						{/* mcg/mg display toggle — drives doseUnitMode; dose stays internal mg */}
						<div
							role="radiogroup"
							aria-label={
								hasNeutralResearchUi
									? t("researchDilution.sampleUnitAriaLabel")
									: t("safety.doseUnitAriaLabel")
							}
							className="bg-muted/50 inline-flex shrink-0 items-center rounded-full border border-border p-0.5"
						>
							{(["mcg", "mg"] as const).map((mode) => {
								const active = doseUnitMode === mode;
								return (
									<label key={mode} className="cursor-pointer">
										<input
											type="radio"
											name={doseUnitGroupName}
											value={mode}
											checked={active}
											onChange={() => onDoseUnitModeChange(mode)}
											className="peer sr-only"
										/>
										<span
											className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded-full px-2 text-[11px] font-semibold transition-colors peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-1 ${
												active
													? "bg-primary text-primary-foreground shadow-sm"
													: "text-muted-foreground hover:text-foreground"
											}`}
										>
											{mode}
										</span>
									</label>
								);
							})}
						</div>
					</div>
				</div>
				{dosePresetsNative.length > 0 ? (
					<PresetChips
						key={`dose-${peptideSlug}-${doseUnitMode}`}
						values={dosePresetsNative}
						selected={doseNative}
						onChange={handleDoseNativeChange}
						unit={doseUnitLabel}
						accessibleLabel={doseFieldLabel}
						min={0}
					/>
				) : (
					<PresetChips
						key={`dose-fallback-${peptideSlug}-${doseUnitMode}`}
						values={[doseNative]}
						selected={doseNative}
						onChange={handleDoseNativeChange}
						unit={doseUnitLabel}
						accessibleLabel={doseFieldLabel}
						min={0}
					/>
				)}
			</div>

			{/* Syringe selection belongs only to the established dose workflow. */}
			{!hasNeutralResearchUi && (
				<div className="space-y-1.5">
					<Label className="text-xs sm:text-sm">{t("form.syringeSize")}</Label>
					<div className="flex gap-2">
						{SYRINGE_OPTIONS.map(({ size, labelIU }) => {
							const active = syringeSize === size;
							return (
								<button
									key={size}
									type="button"
									onClick={() => onSyringeSizeChange(size)}
									aria-pressed={active}
									aria-label={`${size} ml ${t("form.syringeBarrel")}, ${labelIU} IU`}
									className={`flex min-h-14 flex-1 flex-col items-center justify-center rounded-xl border px-2 py-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.98] ${
										active
											? "border-primary bg-primary text-primary-foreground shadow-sm"
											: "hover:border-primary/50 border-border bg-card text-foreground"
									}`}
								>
									<span className="font-mono text-sm font-semibold tabular-nums leading-tight">
										{size} ml
									</span>
									<span className="mt-0.5 font-mono text-[10px] tabular-nums leading-tight opacity-75">
										{labelIU} IU
									</span>
								</button>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
